import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import {
  isSchemaDatapoint,
  isSchemaDatapointButton,
  QueueAction,
} from '@rossum/api-client/schemas';
import { IconBrain } from '@rossum/ui/icons/tabler';
import { Autocomplete, TextField } from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { getSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const MAX_ENTRIES = 3;

const learnMemoryActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    schema_ids: z
      .array(z.string())
      .optional()
      .refine(
        val => val && val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.learnMemory.payload.schemaIds.required',
        })
      ),
  });

const getSelectedSchemaFields = (
  value: z.infer<ReturnType<typeof learnMemoryActionPayloadSchema>>,
  schemaFieldOptions: ReturnType<typeof getSchemaFields>
) => {
  const schemaIds = value.schema_ids;
  if (schemaIds?.length) {
    return schemaFieldOptions.filter(label => schemaIds.includes(label.id));
  }

  return [];
};

const LearnMemoryActionForm = createForm(
  ({ value, onChange, schemaContent, errors }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(field => {
          if (!isSchemaDatapoint(field) || isSchemaDatapointButton(field))
            return false;
          const type = field.uiConfiguration?.type;
          const isComputedField =
            type === 'formula' || type === 'lookup' || type === 'reasoning';
          return isComputedField && field.memory != null;
        }),
      [schemaContent]
    );
    const schemaFields = useMemo(() => {
      return getSelectedSchemaFields(value, schemaFieldOptions);
    }, [schemaFieldOptions, value]);

    return (
      <Autocomplete
        multiple
        size="small"
        options={schemaFieldOptions}
        getOptionLabel={option => `${option.label} (${option.id})`}
        value={schemaFields}
        onChange={(_e, vs) => {
          onChange({
            ...value,
            schema_ids: vs.map(v => v.id),
          });
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.learnMemory.form.fields',
            })}
            placeholder={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.learnMemory.form.chooseFields',
            })}
            InputLabelProps={{ shrink: true }}
            error={getUnsafe(errors, 'schema_ids') !== undefined}
            helperText={getUnsafe(errors, 'schema_ids.message')?.toString()}
          />
        )}
      />
    );
  },
  (intl?: IntlShape) => learnMemoryActionPayloadSchema(intl)
);

const LearnMemoryActionHeader = createHeader(
  ({ value, schemaContent, RenderComponent }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(field => {
          if (!isSchemaDatapoint(field) || isSchemaDatapointButton(field))
            return false;
          const type = field.uiConfiguration?.type;
          const isComputedField =
            type === 'formula' || type === 'lookup' || type === 'reasoning';
          return isComputedField && field.memory != null;
        }),
      [schemaContent]
    );

    const schemaFields = useMemo(() => {
      return getSelectedSchemaFields(value, schemaFieldOptions);
    }, [schemaFieldOptions, value]);

    return (
      <RenderComponent
        entries={[
          ...schemaFields.slice(0, MAX_ENTRIES).flatMap(schemaField => [
            {
              key: `schema_field_${schemaField.id}`,
              label: intl.formatMessage({
                id: 'features.rules.testSection.displayKey.schemaFields',
              }),
              value: schemaField.label,
            },
          ]),
          ...(schemaFields.length > MAX_ENTRIES
            ? [
                {
                  key: 'more_schema_fields',
                  value: `+${schemaFields.length - MAX_ENTRIES}`,
                },
              ]
            : []),
        ]}
      />
    );
  },
  (intl?: IntlShape) => learnMemoryActionPayloadSchema(intl)
);

export const learnMemoryAction = {
  icon: IconBrain,
  intlKey: 'learnMemory' as const,
  resolveAction: (a: QueueAction) => a.type === 'learn_memory',
  serialized: { type: 'learn_memory' as const },
  groupIntlKey: 'memory' as const,
  form: LearnMemoryActionForm,
  header: LearnMemoryActionHeader,
  supportedEvents: [
    'annotation_confirmed',
    'annotation_exported',
    'annotation_export_succeeded',
  ] as const,
  payloadSchema: (intl?: IntlShape) => learnMemoryActionPayloadSchema(intl),
};
